import React from "react";
const { types } = require('sharetribe-flex-sdk');
const { Money } = types;

import ContinueIcon from '../../images/continue-icon.svg';
import css from './OwnerPlans.module.css';
import { formattedPrice, priceData } from "../../../ListingPage/ListingPage.shared";
import { formatCurrencyMajorUnit } from "../../../../util/currency";
import { subscriptionTypes } from "../../../../util/subscriptionsHelpers";
import { CollapseFAQ, NamedLink } from "../../../../components";
import SectionCoupon from "../SectionCoupon/SectionCoupon";
import TrustWidgetBig from "../../../../components/StaticPageContainers/TrustWidgetBig";

const OwnerPlans = props => {
    const { subscriptionPrices, createSubscription, intl, coupon,
        couponError,
        handleChange,
        promoCode,
        isBusy,
        onApplyCoupon,
    } = props;


    // first filter out the ones without nickname
    const subscriptionPricesNew = subscriptionPrices?.filter(p => p.nickname);

    const premiumSubscription = subscriptionPricesNew?.find(p => p.nickname.includes(subscriptionTypes.premium));
    const choiceSubscription = subscriptionPricesNew?.find(p => p.nickname.includes(subscriptionTypes.choice));
    const valueSubscription = subscriptionPricesNew?.find(p => p.nickname.includes(subscriptionTypes.value));

    const premiumSubscriptionFormattedPrice = premiumSubscription &&
        formatCurrencyMajorUnit(intl, premiumSubscription.currency.toUpperCase(), premiumSubscription.unit_amount / 100);
    const premiumSubscriptionFormattedPriceWithCoupon = coupon && premiumSubscription &&
        formatCurrencyMajorUnit(intl, premiumSubscription.currency.toUpperCase(), (premiumSubscription.unit_amount - (premiumSubscription.unit_amount * coupon.percent_off) / 100) / 100);

    const choiceSubscriptionFormattedPrice = choiceSubscription &&
        formatCurrencyMajorUnit(intl, choiceSubscription.currency.toUpperCase(), choiceSubscription.unit_amount / 100);
    const choiceSubscriptionFormattedPriceWithCoupon = coupon && choiceSubscription &&
        formatCurrencyMajorUnit(intl, choiceSubscription.currency.toUpperCase(), (choiceSubscription.unit_amount - (choiceSubscription.unit_amount * coupon.percent_off) / 100) / 100);


    const valueSubscriptionFormattedPrice = valueSubscription &&
        formatCurrencyMajorUnit(intl, valueSubscription.currency.toUpperCase(), valueSubscription.unit_amount / 100);
    const valueSubscriptionFormattedPriceWithCoupon = coupon && valueSubscription &&
        formatCurrencyMajorUnit(intl, valueSubscription.currency.toUpperCase(), (valueSubscription.unit_amount - (valueSubscription.unit_amount * coupon.percent_off) / 100) / 100);

    const scrollToCompare = (e) => {
        e.preventDefault();
        const compareSection = document.getElementById('compare');
        if (compareSection) {
            compareSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const faqs = [
        {
            question: "Which plan is right for me as a first-time user?",
            answer: "Value is ideal if you have one property and travel occasionally. Choice adds the 24/7 Vet Advice Line and extra properties, which many owners prefer. Premium adds a listing boost for maximum visibility.",
        },
        {
            question: "Do I need a membership to contact sitters or post a request?",
            answer: "Yes. Registration is free and a paid membership is required to post your sit and message verified sitters.",
        },
        {
            question: "Can I upgrade my membership plan later?",
            answer: "Yes. You can upgrade at any time. If you move up a tier, your remaining term carries over under our upgrade policy.",
        },
        {
            question: "Is the 24/7 Vet Advice Line included on every plan?",
            answer: "It is included on Choice and Premium.",
        },
        {
            question: "How many properties can I list on each plan?",
            answer: "Value is for a single property. Choice and Premium allow up to three properties.",
        },
        {
            question: "Do you offer discounts or coupons?",
            answer: "When available, you can apply a code during signup. If you do not have a code, you can complete signup without one.",
        },
    ];

    return (
        <div  >

            <div className={css.introSection}>
                <div className={css.containerHero}>
                    <h1 className={css.title}>
                        Homeowner Membership Plans & Pricing
                    </h1>
                    <p className={css.subtitle}>
                        Compare features and pricing to pick the plan that lets you post your sit and contact verified house sitters.
                    </p>

                    <h3 className={css.whatYouGetTitle}>
                        At a glance
                    </h3>

                    <div className={css.whatYouGetGrid}>
                        <div className={css.whatYouGetItem}>
                            <div className={css.featureIcon}>🏡</div>
                            Post your sit request
                        </div>
                        <div className={css.whatYouGetItem}>
                            <div className={css.featureIcon}>💬</div>
                            Contact verified sitters in one place
                        </div>
                        <div className={css.whatYouGetItem}>
                            <div className={css.featureIcon}>⭐</div>
                            Reviews and references on profiles
                        </div>
                        <div className={css.whatYouGetItem}>
                            <div className={css.featureIcon}>📞</div>
                            24/7 Vet Advice Line on Choice and Premium
                        </div>
                    </div>

                    <div className={css.introActions}>
                        <button onClick={scrollToCompare} className={css.compareAnchorButton}>
                            Compare plan features
                        </button>
                        <p className={css.helperText}>
                            New to house sitting? •{' '}
                            <NamedLink name="WhatIsHouseSittingPage" className={css.helperLink}>
                                See how it works
                            </NamedLink>
                        </p>
                    </div>
                </div>
            </div>

            <div className={css.root}>

                {/* Compare Features Section */}
                <div id="compare" className={css.compareSection}>
                    <h2 className={css.compareSectionTitle}>
                        Compare Plan Features
                    </h2>
                    <p className={css.compareSectionSubtitle}>
                        See what changes by tier so you can choose the best fit for your travel plans.
                    </p>

                    <div className={css.compareTable}>
                        <table className={css.table}>
                            <thead>
                                <tr>
                                    <th>Feature</th>
                                    <th>Value</th>
                                    <th className={css.popularColumn}>Choice<br /><span className={css.popularBadge}>Most popular</span></th>
                                    <th>Premium</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Properties you can list</td>
                                    <td>1</td>
                                    <td>Up to 3</td>
                                    <td>Up to 3</td>
                                </tr>
                                <tr>
                                    <td>24/7 Vet Advice Line</td>
                                    <td>—</td>
                                    <td className={css.included}>✓</td>
                                    <td className={css.included}>✓</td>
                                </tr>
                                <tr>
                                    <td>Listing boost/priority</td>
                                    <td>—</td>
                                    <td className={css.included}>✓</td>
                                    <td className={css.included}>✓</td>
                                </tr>
                                <tr>
                                    <td>Discounts for upgrades</td>
                                    <td className={css.included}>✓</td>
                                    <td className={css.included}>✓</td>
                                    <td className={css.included}>✓</td>
                                </tr>
                                <tr>
                                    <td>On-platform video calls</td>
                                    <td className={css.included}>✓</td>
                                    <td className={css.included}>✓</td>
                                    <td className={css.included}>✓</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p className={css.compareFootnote}>
                        All plans include reviews and references on  <NamedLink className={css.link}
                            name="SearchHouseSitterPage">
                            sitter profiles
                        </NamedLink> and access to our verified community. You can upgrade at any time.
                    </p>
                </div>

                <div className={css.plansContainer}>
                    <div className={css.plansHeading}>
                        <h2>Home Owner Membership Options</h2>
                    </div>

                    <div className={css.container}>
                        {/* Value Plan */}
                        <div className={css.card}>
                            <div className={css.cardTitle}>
                                Value Match
                            </div>

                            <div className={css.planDescription}>
                                <p><strong>Best for occasional sits and a simple, first-time setup.</strong></p>
                                <p>You get: the essentials to post your sit and confirm a trusted match.</p>
                                <p>Perks: discounts available if you upgrade later.</p>
                            </div>

                            <div>
                                <div className={coupon ? css.cardPriceDiscounted : css.cardPrice}>
                                    <span className={css.price}>
                                        {valueSubscriptionFormattedPrice}
                                    </span>
                                    per year
                                </div>
                                {coupon &&
                                    <div className={css.cardPrice}>
                                        <span className={css.price}>
                                            {valueSubscriptionFormattedPriceWithCoupon}
                                        </span>
                                        per first year
                                    </div>
                                }
                            </div>

                            <div className={css.button}
                                onClick={() => createSubscription(valueSubscription.id)}>
                                Continue with Value
                                <img className={css.icon} src={ContinueIcon} />
                            </div>
                        </div>

                        {/* Choice Plan - Most Popular */}
                        <div className={css.cardOffest}>
                            <div className={css.tag}>
                                Most popular
                            </div>
                            <div className={css.cardTitle}>
                                Choice Match
                            </div>

                            <div className={css.planDescription}>
                                <p><strong>Best for regular travellers or multi-property owners.</strong></p>
                                <p>You get: up to 3 properties and the 24/7 Vet Advice Line for extra peace of mind.</p>
                                <p>Perks: easy upgrades and all core features owners rely on.</p>
                            </div>

                            <div>
                                <div className={coupon ? css.cardPriceDiscounted : css.cardPrice}>
                                    <span className={css.price}>
                                        {choiceSubscriptionFormattedPrice}
                                    </span>
                                    per year
                                </div>
                                {coupon &&
                                    <div className={css.cardPrice}>
                                        <span className={css.price}>
                                            {choiceSubscriptionFormattedPriceWithCoupon}
                                        </span>
                                        per first year
                                    </div>
                                }
                            </div>

                            <div className={css.button}
                                onClick={() => createSubscription(choiceSubscription.id)}>
                                Continue with Choice
                                <img className={css.icon} src={ContinueIcon} />
                            </div>
                        </div>

                        {/* Premium Plan */}
                        <div className={css.card}>
                            <div className={css.cardTitle}>
                                Premium Match
                            </div>

                            <div className={css.planDescription}>
                                <p><strong>Best for frequent sits and maximum visibility.</strong></p>
                                <p>You get: up to 3 properties, the 24/7 Vet Advice Line and a listing boost/priority placement.</p>
                                <p>Perks: discount options for plan upgrades when needed.</p>
                            </div>

                            <div>
                                <div className={coupon ? css.cardPriceDiscounted : css.cardPrice}>
                                    <span className={css.price}>
                                        {premiumSubscriptionFormattedPrice}
                                    </span>
                                    per year
                                </div>
                                {coupon &&
                                    <div className={css.cardPrice}>
                                        <span className={css.price}>
                                            {premiumSubscriptionFormattedPriceWithCoupon}
                                        </span>
                                        per first year
                                    </div>
                                }
                            </div>

                            <div className={css.button}
                                onClick={() => createSubscription(premiumSubscription.id)}>
                                Continue with Premium
                                <img className={css.icon} src={ContinueIcon} />
                            </div>
                        </div>
                    </div>

                    <SectionCoupon
                        intl={intl}
                        coupon={coupon}
                        couponError={couponError}
                        handleChange={handleChange}
                        promoCode={promoCode}
                        isBusy={isBusy}
                        onApplyCoupon={onApplyCoupon}
                    />

                </div>



                {/* Included in Every Plan Section */}
                <div className={css.includedSection}>
                    <h2 className={css.includedTitle}>
                        Included in Every Plan
                    </h2>
                    <p className={css.includedSubtitle}>
                        All homeowner memberships give you the essentials to post a sit and match with verified house sitters.
                    </p>

                    <ul className={css.includedList}>
                        <li>
                            🗓️ Post your sit request and set travel dates
                        </li>
                        <li>
                            ⭐ <NamedLink className={css.link}
                                name="SearchHouseSitterPage">
                                View sitter profiles
                            </NamedLink> with reviews and references
                        </li>
                        <li>
                            🔒 Secure on-platform messaging and onsite video calls with members
                        </li>
                        <li>
                            👀 Visibility of who viewed and bookmarked your listing
                        </li>
                        <li>
                            👥 Access to our verified community and UK-based support
                        </li>
                    </ul>
                </div>

                {/* Trust Section */}
                <div className={css.trustSection}>
                    <h3 className={css.trustTitle}>
                        Trusted by Homeowners
                    </h3>
                    <p className={css.trustSubtitle}>
                        Rated Excellent on Trustpilot and supported by a {' '}
                        <NamedLink name="AboutHouseSitMatchPage" className={css.helperLink}>
                            friendly, UK-based team
                        </NamedLink>. Read what members say about booking sitters through HouseSit Match.
                    </p>
                    {/* <a className={css.compareAnchorButton}
                        href="https://uk.trustpilot.com/review/housesitmatch.com"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Read reviews on Trustpilot
                    </a> */}
                    <TrustWidgetBig />
                </div>

                {/* FAQs Section */}
                <div className={css.faqSection}>
                    <h2 className={css.faqTitle}>
                        FAQs About Homeowner Membership Plans
                    </h2>

                    <div className={css.faqList}>
                        <CollapseFAQ list={faqs} />
                    </div>
                </div>

            </div>



        </div >
    );
};

export default OwnerPlans;